// Workout Log motion helpers. Presentation only: nothing here reads or writes
// the execution. They reuse the Design V2 motion language (tokens.css):
// --lz-v2-motion-fast 160 ms, --lz-v2-motion-standard 240 ms and
// --lz-v2-ease-standard. CSS handles everything it can; these helpers cover
// the two things CSS cannot do on every supported browser: animating to and
// from an element's natural height, and animating a layout that snaps (FLIP).

export const MOTION = Object.freeze({
  fast: 160,
  exit: 180,
  layout: 220,
  standard: 240,
  easing: "cubic-bezier(0.2, 0, 0, 1)",
});

// False under prefers-reduced-motion, and wherever the Web Animations API or
// matchMedia is missing (so the state change is simply immediate).
export function motionAllowed() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  if (typeof Element === "undefined" || typeof Element.prototype.animate !== "function") return false;
  // Animations do not advance in a hidden page: change state at once instead.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
  try {
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_error) {
    return false;
  }
}

function cancelAnimations(element) {
  element.getAnimations?.().forEach((animation) => animation.cancel());
}

function boxFrames(element, size, axis = "height") {
  const style = window.getComputedStyle(element);
  if (axis === "width") {
    // A flex item folding away sideways also gives back the gap beside it.
    const gap = parseFloat(window.getComputedStyle(element.parentElement).columnGap) || 0;
    return [
      { width: "0px", minWidth: "0px", paddingLeft: "0px", paddingRight: "0px", marginRight: `${-gap}px`, opacity: 0 },
      {
        width: `${size}px`,
        minWidth: style.minWidth,
        paddingLeft: style.paddingLeft,
        paddingRight: style.paddingRight,
        marginRight: style.marginRight,
        opacity: 1,
      },
    ];
  }
  return [
    { height: "0px", minHeight: "0px", paddingTop: "0px", paddingBottom: "0px", opacity: 0 },
    {
      height: `${size}px`,
      minHeight: style.minHeight,
      paddingTop: style.paddingTop,
      paddingBottom: style.paddingBottom,
      opacity: 1,
    },
  ];
}

// Grows an element that is already in the layout from zero to its natural
// height. The element is interactive throughout.
export function expandIn(element, { duration = MOTION.layout, axis = "height" } = {}) {
  if (!element || !motionAllowed()) return;
  cancelAnimations(element);
  const height = element.getBoundingClientRect()[axis];
  if (!height) return;
  const previousOverflow = element.style.overflow;
  element.style.overflow = "hidden";
  const animation = element.animate(boxFrames(element, height, axis), {
    duration,
    easing: MOTION.easing,
  });
  const restore = () => { element.style.overflow = previousOverflow; };
  animation.finished.then(restore, restore);
}

// Collapses an element to zero height and resolves when it is gone (true) or
// when the collapse was interrupted (false). The caller then removes it; if it
// is still mounted two frames later it is restored, so nothing can stay
// invisibly collapsed.
export function collapseOut(element, { duration = MOTION.exit, axis = "height" } = {}) {
  if (!element || !motionAllowed()) return Promise.resolve(true);
  const height = element.getBoundingClientRect()[axis];
  cancelAnimations(element);
  if (!height) return Promise.resolve(true);
  const previous = { overflow: element.style.overflow, pointerEvents: element.style.pointerEvents };
  element.style.overflow = "hidden";
  element.style.pointerEvents = "none";
  const animation = element.animate(boxFrames(element, height, axis).reverse(), {
    duration,
    easing: MOTION.easing,
    fill: "forwards",
  });
  const restore = () => {
    element.style.overflow = previous.overflow;
    element.style.pointerEvents = previous.pointerEvents;
  };
  // The timer is a backstop: if the page is hidden mid-collapse the animation
  // stops advancing, and the state change must still go through.
  let timer = null;
  const timeout = new Promise((resolve) => {
    timer = window.setTimeout(() => resolve(true), duration + 150);
  });
  const finished = animation.finished.then(() => true, () => false);
  return Promise.race([finished, timeout]).then((completed) => {
    window.clearTimeout(timer);
    if (!completed) {
      restore();
      return false;
    }
    // Released a moment later, once the caller has removed the element; if it
    // is still mounted it simply comes back.
    window.setTimeout(() => { animation.cancel(); restore(); }, 60);
    return true;
  });
}

// FLIP: plays each element from where it was (`first`, a Map of element ->
// DOMRect and font size captured before the layout changed) to where it is.
export function playFrom(first, { duration = MOTION.layout, scale: scaled = true } = {}) {
  if (!motionAllowed()) return;
  first.forEach((before, element) => {
    if (!element.isConnected) return;
    const after = element.getBoundingClientRect();
    if (!after.width || !before.rect.width) return;
    const fontSize = parseFloat(window.getComputedStyle(element).fontSize) || 0;
    const scale = scaled && before.fontSize && fontSize ? before.fontSize / fontSize : 1;
    const dx = before.rect.left - after.left;
    const dy = before.rect.top - after.top;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(scale - 1) < 0.01) return;
    cancelAnimations(element);
    element.animate(
      [
        { transformOrigin: "left top", transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
        { transformOrigin: "left top", transform: "none" },
      ],
      { duration, easing: MOTION.easing }
    );
  });
}

export function captureRects(elements) {
  const first = new Map();
  elements.forEach((element) => {
    if (!element) return;
    first.set(element, {
      rect: element.getBoundingClientRect(),
      fontSize: parseFloat(window.getComputedStyle(element).fontSize) || 0,
    });
  });
  return first;
}

// The sticky header changes height in one step. This makes its bottom edge
// travel instead: when it got shorter, a strip the size of the difference
// retracts under it; when it got taller, the new part is revealed downward.
// `delta` is old height minus new height.
export function settleChromeEdge(chrome, delta, { duration = MOTION.layout } = {}) {
  if (!chrome || !motionAllowed() || Math.abs(delta) < 1) return;
  chrome.querySelectorAll(":scope > [data-chrome-settle]").forEach((node) => node.remove());
  const options = { duration, easing: MOTION.easing };
  if (delta > 0) {
    const style = window.getComputedStyle(chrome);
    const strip = chrome.ownerDocument.createElement("span");
    strip.setAttribute("data-chrome-settle", "");
    strip.setAttribute("aria-hidden", "true");
    Object.assign(strip.style, {
      position: "absolute",
      top: "100%",
      left: "0",
      right: "0",
      height: `${delta}px`,
      pointerEvents: "none",
      background: style.backgroundColor,
      borderBottom: `${style.borderBottomWidth} solid ${style.borderBottomColor}`,
      transformOrigin: "top",
    });
    chrome.appendChild(strip);
    const remove = () => strip.remove();
    strip.animate([{ transform: "scaleY(1)" }, { transform: "scaleY(0)" }], options)
      .finished.then(remove, remove);
    return;
  }
  chrome.animate(
    [
      { clipPath: `inset(0px 0px ${-delta}px 0px)` },
      { clipPath: "inset(0px 0px 0px 0px)" },
    ],
    options
  );
}
